import { bind } from '@react-rxjs/core'
import { createSignal } from '@react-rxjs/utils'
import { combineLatest, filter, map, of, startWith, switchMap, take } from 'rxjs'

import { isNotNull } from '../../../utils/collectionTools'
import { switchMapRight } from '../../../utils/rx/operators'
import { FetchResult } from '../../fetch'
import {
  PostHeroImageOptionInputData,
  PostHeroTextOptionInputData,
  PutDispensaryInputData,
} from '../../sanity/types'
import { latestDispensarySlug$ } from '../state'
import { dispensaryUpdateFactory } from './dispensary'
import { heroImageOptionUpsertFactory } from './hero-image-options'
import { heroTextOptionUpsertFactory } from './hero-text-options'

export interface MergedUpdateDispensaryInput {
  heroImageOption?: Omit<PostHeroImageOptionInputData, 'slug'>
  heroTextOption?: Omit<PostHeroTextOptionInputData, 'slug'>
  dispensary: Omit<PutDispensaryInputData, 'slug'>
}

const [mergedUpdateDispensaryInput$, setMergedUpdateDispensaryInput] =
  createSignal<MergedUpdateDispensaryInput>()

export { setMergedUpdateDispensaryInput }

/**
 * Update Sanity dispensary document and dependent hero image and text options (if any) as a
 * combined operation
 */
export const [useSanityDispensaryMergedUpdate, sanityDispensaryMergedUpdate$] = bind<{
  loading: boolean
  result: FetchResult<unknown> | undefined
}>(
  combineLatest([latestDispensarySlug$.pipe(filter(isNotNull)), mergedUpdateDispensaryInput$]).pipe(
    switchMap(([slug, mergedUpdateInput]) =>
      combineLatest([
        // update hero image option (if any)
        mergedUpdateInput.heroImageOption
          ? heroImageOptionUpsertFactory({ ...mergedUpdateInput.heroImageOption, slug }).pipe(
              // get updated hero image option
              switchMapRight(({ data }) => data),
              take(1),
            )
          : of({ _tag: 'Right' as const, data: undefined }),
        mergedUpdateInput.heroTextOption
          ? heroTextOptionUpsertFactory({ ...mergedUpdateInput.heroTextOption, slug }).pipe(
              // get updated hero text option
              switchMapRight(({ data }) => data),
              take(1),
            )
          : of({ _tag: 'Right' as const, data: undefined }),
      ]).pipe(
        map(
          ([heroImageOptionUpsert, heroTextOptionUpsert]): FetchResult<
            [
              Extract<typeof heroImageOptionUpsert, { _tag: 'Right' }>['data'],
              Extract<typeof heroTextOptionUpsert, { _tag: 'Right' }>['data'],
            ]
          > =>
            heroImageOptionUpsert._tag === 'Left'
              ? heroImageOptionUpsert
              : heroTextOptionUpsert._tag === 'Left'
                ? heroTextOptionUpsert
                : {
                    _tag: 'Right' as const,
                    data: [heroImageOptionUpsert.data, heroTextOptionUpsert.data],
                  },
        ),
        switchMapRight(({ data: [newHeroImageOption, newHeroTextOption] }) =>
          dispensaryUpdateFactory(slug, {
            method: 'PUT',
            input: {
              ...mergedUpdateInput.dispensary,
              // set hero image and text references to upserted options
              ...(newHeroImageOption && { heroImageRef: newHeroImageOption._id }),
              ...(newHeroTextOption && { heroTextRef: newHeroTextOption._id }),
            },
          }),
        ),
        map((result) => ({
          loading: false,
          result,
        })),
        startWith({
          loading: true,
          result: undefined,
        }),
      ),
    ),
  ),
  {
    loading: false,
    result: undefined,
  },
)
